import { useEffect, useRef, useState } from 'react';
import { useLastUpdate } from '@features/dashboard/hooks/useLastUpdate';
import { liveQuotesStore } from '@features/quotes/liveQuotesStore';
import { useAccount } from '@shared/state/accountStore';
import { tzFormat } from '@shared/lib/userTz';

/**
 * Global "Last update" data-freshness stamp, rendered in the {@link Topbar} on EVERY tab (not just
 * the dashboard). 2026-07-09 rename from "Last import": it now advances on ANY data received from
 * CQG — new trades AND live price updates — not just imports. Scoped to the globally-selected
 * ACCOUNT only (NOT the date range — freshness is independent of the view window).
 *
 * Two sources, the LATER one wins:
 *  - the durable BE watermark (`/stats/last-update`, MAX(updated_at) over trades+correlations),
 *    which ticks live on `trades.changed` / `correlations.changed` and survives offline sessions;
 *  - the last live-quote arrival (`liveQuotesStore.subscribeAll`), throttled to ≤1 state update
 *    per 60s — the display is minute-precision, so re-rendering per tick would be pure churn.
 * While CQG is live the stamp therefore advances every minute; offline it degrades to the trade
 * watermark (the standing live+offline design). Renders nothing until either source has data.
 *
 * (No refresh glyph/button — the realtime bus + per-view polling keep the numbers fresh, so a
 * refresh affordance is meaningless. The decorative gradient glyph was removed per product 2026-07-18.)
 */
export function LastImportStamp() {
  const account = useAccount();
  const lu = useLastUpdate({ account: account || undefined });

  // Last live-quote arrival, throttled: first quote updates immediately (feed just came up),
  // then at most once per 60s. The ref carries the throttle clock across renders.
  const [lastQuoteAt, setLastQuoteAt] = useState<Date | null>(null);
  const lastSetRef = useRef(0);
  useEffect(() => liveQuotesStore.subscribeAll(() => {
    const now = Date.now();
    if (now - lastSetRef.current < 60_000) return;
    lastSetRef.current = now;
    setLastQuoteAt(new Date(now));
  }), []);

  const watermark = lu.data?.lastUpdateAt ? new Date(lu.data.lastUpdateAt) : null;
  const at = lastQuoteAt && (!watermark || lastQuoteAt > watermark) ? lastQuoteAt : watermark;
  if (!at) return null;

  return (
    <div className="hidden items-center md:flex" title="Last time data was received (trades or live prices)">
      <span className="text-[10px] font-bold uppercase leading-3 tracking-wider text-text-muted">
        Last update: {formatLastUpdate(at)}
      </span>
    </div>
  );
}

// User-profile timezone, like every other timestamp in the app (userTz).
function formatLastUpdate(d: Date): string {
  return tzFormat(d, {
    month: 'short', day: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}
